import React, { useEffect, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import { DataProviderInstance } from '../../../api/DataProvider';
import AreaTitle from '../../../components/areaTitle/AreaTitle';
import CustomButton from '../../../components/buttons/CustomButton';
import usePlanLock from '../../../hooks/usePlanLock';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import Toast from '../../../components/support/Toast';
import { SUBSCRIPTION_PERMISSIONS } from '../../../consts/SubscriptionFeatures';
import { BUSINESS_PLAN_NAME } from '../../../components/subscription/planLockTexts';
import CampaignRow from './CampaignRow';
import CreateEditCampaignPopup from './CreateEditCampaignPopup';
import MarketingListCard from '../shared/MarketingListCard';
import MarketingEmptyState from '../shared/MarketingEmptyState';
import MarketingListSkeleton from '../shared/MarketingListSkeleton';
import shared from '../shared/MarketingShared.module.css';

function Campaigns() {
  const { GetCampaigns } = DataProviderInstance();

  const [loaded, setLoaded] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [createOrEditPopupVisibility, setCreateOrEditPopupVisibility] = useState(false);
  const [needsReload, setNeedsReload] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  // Cadeado por plano (Business): as campanhas continuam visíveis; criar,
  // editar e excluir abrem o modal "Falar com a gente".
  const lock = usePlanLock(SUBSCRIPTION_PERMISSIONS.MARKETING, 'O cadastro de campanhas', BUSINESS_PLAN_NAME);

  // Plano ainda não conhecido: a tela espera junto com a lista, para o
  // banner do cadeado aparecer com o conteúdo (nunca parecer liberada)
  const { loading: planLoading, planConfig } = useSubscription();
  const planReady = !planLoading || planConfig !== null;

  useEffect(() => {
    setNeedsReload(true);
  }, []);

  useEffect(() => {
    if (needsReload) {
      setLoaded(false);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsReload]);

  const loadData = () => {
    GetCampaigns((result) => {
      setCampaigns(result.data);
      setLoaded(true);
      setNeedsReload(false);
    });
  };

  useEffect(() => {
    const filteredCampaigns = campaigns.filter((campaign) => {
      return campaign.name.toLowerCase().includes(searchTerm.toLowerCase());
    });

    setFiltered(filteredCampaigns);
  }, [searchTerm, campaigns]);

  function showCreateEditCampaignPopup(campaignData) {
    setCurrentCampaign(campaignData);
    setCreateOrEditPopupVisibility(true);
  }

  function onCreateEditCampaignPopupHide() {
    setCurrentCampaign(null);
    setCreateOrEditPopupVisibility(false);
  }

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  function onCampaignUpdate(response, isEdit) {
    setNeedsReload(true);
    showToast(isEdit ? 'Campanha atualizada' : 'Campanha criada');
  }

  function onCampaignDeleted() {
    setNeedsReload(true);
    showToast('Campanha excluída');
  }

  function onCreateCampaignClickHandler() {
    setCurrentCampaign(null);
    setCreateOrEditPopupVisibility(true);
  }

  // Na recarga depois de salvar, a lista atual fica na tela (sem piscar)
  const firstLoad = (!loaded && campaigns.length === 0) || !planReady;
  const isEmpty = !firstLoad && loaded && campaigns.length === 0;

  return (
    <div className='page-content'>
      <div className={shared.page}>
        <div className={shared.pageHeader}>
          <AreaTitle
            Title='Campanhas'
            Description="Organize suas ações de divulgação e saiba qual delas trouxe cada lead."
            Icon={<CampaignOutlinedIcon />}
          />
          <CustomButton
            icon={<AddRoundedIcon />}
            variant="contained"
            style="main"
            enabled={planReady}
            label="Nova campanha"
            onClick={lock.guard(onCreateCampaignClickHandler)}
          />
        </div>
        {lock.banner}
        {lock.modal}

        <MarketingListCard
          title="Campanhas cadastradas"
          count={firstLoad ? null : campaigns.length}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          showSearch={!firstLoad && !isEmpty}
        >
          {firstLoad && <MarketingListSkeleton />}
          {isEmpty && (
            <MarketingEmptyState
              step="campaign"
              title="Nenhuma campanha ainda"
              text="Crie uma campanha para cada ação de divulgação. Com a mensagem padrão, cada lead já chega identificado."
              actionLabel="Criar primeira campanha"
              onAction={lock.guard(onCreateCampaignClickHandler)}
            />
          )}
          {!firstLoad && !isEmpty && (
            filtered.length === 0 ? (
              <MarketingEmptyState searching />
            ) : (
              <div className={shared.scroll}>
                <ul className={shared.list}>
                  {filtered.map((campaignData, index) => (
                    <CampaignRow
                      key={campaignData.id ?? index}
                      campaignData={campaignData}
                      onEdit={lock.guard(() => showCreateEditCampaignPopup(campaignData))}
                      onUpdate={onCampaignDeleted}
                      onBlockedAction={lock.locked ? lock.openUpgrade : null}
                    />
                  ))}
                </ul>
              </div>
            )
          )}
        </MarketingListCard>

        {createOrEditPopupVisibility && (
          <CreateEditCampaignPopup
            OnHide={onCreateEditCampaignPopupHide}
            OnUpdate={onCampaignUpdate}
            Data={currentCampaign}
          />
        )}

        <Toast
          open={toast.open}
          onClose={() => setToast({ ...toast, open: false })}
          message={toast.message}
          severity={toast.severity}
        />
      </div>
    </div>
  );
}

export default Campaigns;
